import { useState } from 'react';
import type { ReactNode } from 'react';
import { Creator } from '@/types/entities';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { useI18n } from '@/i18n';

export interface CreatorFormValues {
  fullName: string;
  tiktokUsername: string;
  phone: string;
  whatsapp: string;
  email: string;
  country: string;
  emirate: string;
  language: string;
  status: string;
  nextFollowup: string;
  nextAction: string;
  notes: string;
}

const STATUS_OPTIONS = ['new', 'contacted', 'interested', 'application', 'pending_tiktok', 'joined', 'onboarding', 'active', 'at_risk', 'inactive'];

function toValues(c?: Creator | null): CreatorFormValues {
  return {
    fullName: c?.fullName ?? '',
    tiktokUsername: c?.tiktokUsername ?? '',
    phone: c?.phone ?? '',
    whatsapp: c?.whatsapp ?? '',
    email: c?.email ?? '',
    country: c?.country ?? '',
    emirate: c?.emirate ?? '',
    language: c?.language ?? '',
    status: c?.status ?? 'new',
    nextFollowup: c?.nextFollowup ?? '',
    nextAction: c?.nextAction ?? '',
    notes: c?.notes ?? '',
  };
}

/** Create AND edit share this one form — the only difference is whether an initial Creator is passed in. */
export function CreatorFormDrawer({
  creator,
  onClose,
  onSubmit,
}: {
  creator?: Creator | null;
  onClose: () => void;
  onSubmit: (values: CreatorFormValues) => void;
}) {
  const { t } = useI18n();
  const [values, setValues] = useState<CreatorFormValues>(toValues(creator));
  const [errors, setErrors] = useState<Record<string, string>>({});

  function set<K extends keyof CreatorFormValues>(key: K, val: CreatorFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!values.fullName.trim()) next.fullName = 'Required';
    if (!values.tiktokUsername.trim()) next.tiktokUsername = 'Required';
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = 'Invalid email';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  return (
    <EntityDrawer title={creator ? t('creatorForm.editTitle') : t('creatorForm.createTitle')} onClose={onClose}>
      <Field label={t('quickCreate.fullName')} required error={errors.fullName}>
        <input className="text-input" value={values.fullName} onChange={(e) => set('fullName', e.target.value)} />
      </Field>
      <Field label={t('quickCreate.tiktok')} required error={errors.tiktokUsername}>
        <input className="text-input" value={values.tiktokUsername} onChange={(e) => set('tiktokUsername', e.target.value)} />
      </Field>
      <Field label="Phone"><input className="text-input" value={values.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
      <Field label="WhatsApp"><input className="text-input" value={values.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} /></Field>
      <Field label="Email" error={errors.email}><input className="text-input" value={values.email} onChange={(e) => set('email', e.target.value)} /></Field>
      <Field label="Country"><input className="text-input" value={values.country} onChange={(e) => set('country', e.target.value)} /></Field>
      <Field label="Emirate"><input className="text-input" value={values.emirate} onChange={(e) => set('emirate', e.target.value)} /></Field>
      <Field label="Status">
        <select className="text-input" value={values.status} onChange={(e) => set('status', e.target.value)}>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{t(`status.creator.${s.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}</option>)}
        </select>
      </Field>
      <Field label={t('detail.nextFollowup')}><input type="date" className="text-input" value={values.nextFollowup} onChange={(e) => set('nextFollowup', e.target.value)} /></Field>
      <Field label={t('detail.nextAction')}><input className="text-input" value={values.nextAction} onChange={(e) => set('nextAction', e.target.value)} /></Field>
      <Field label="Notes"><textarea className="text-input" rows={3} value={values.notes} onChange={(e) => set('notes', e.target.value)} /></Field>

      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button className="btn-primary" onClick={() => validate() && onSubmit(values)}>{t('common.save')}</button>
        <button onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </EntityDrawer>
  );
}

function Field({ label, required, error, children }: { label: string; required?: boolean; error?: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{label}{required && <span style={{ color: 'var(--crit)' }}> *</span>}</label>
      {children}
      {error && <div style={{ fontSize: 11, color: 'var(--crit)', marginTop: 3 }}>{error}</div>}
    </div>
  );
}
